import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS as dndCSS } from "@dnd-kit/utilities";
import { DotShaderBackground } from "./DotShaderBackground";
import HowItWorksPage from "./pages/HowItWorks";
import BlogPage from "./pages/Blog";
import AboutPage from "./pages/About";
import ContactPage from "./pages/Contact";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import TermsOfServicePage from "./pages/TermsOfService";
import {
  uploadTrack,
  importPlaylist,
  getTracks,
  getAnalysisStatus,
  generateSet,
  deleteTrack,
  deleteAllTracks,
  submitFeedback,
  submitEmailCapture,
  logReorder,
  saveSet,
  getPublicSiteOrigin,
  getSharedSet,
  getSharedSetOgImageUrl,
} from "./api/client";
import { downloadSetPdf } from "./lib/downloadSetPdf";
import { Track, DjSet, TransitionSuggestion } from "./types";
import { Analytics } from "@vercel/analytics/react";
import { MAX_TRACKS_PER_SESSION, MAX_UPLOAD_FILE_BYTES, MAX_UPLOAD_FILE_MB } from "./constants/limits";
import { buildWhyLine, parseSetProfileNarrative } from "./lib/transitionNarrative";
import type { FileRejection } from "react-dropzone";
import { Routes, Route, useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// ── Helpers ───────────────────────────────────────────────────────────────────

const ONBOARDING_KEY = "setpilot_seen_onboarding";
/** "Maybe later" — hide soft ask until the browser session ends. */
const EMAIL_SOFT_DISMISS_KEY = "setpilot_email_soft_dismiss";
const HISTORY_RESULT_STATE = "setpilot-result-view";

/** Header/footer vinyl art; tab icon uses `public/favicon.svg` (ring + inset) wrapping this PNG. */
const LOGO_IMG_SRC = `${process.env.PUBLIC_URL || ""}/favicon.png`;

function fmtDuration(secs: number | null | undefined): string {
  if (secs == null) return "--:--";
  const n = Number(secs);
  if (!Number.isFinite(n) || n < 0) return "--:--";
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Match client File.name to server originalFilename (handles DB truncation / encoding). */
function trackMatchesFile(tr: { originalFilename: string }, file: File): boolean {
  const s = tr.originalFilename;
  const n = file.name;
  if (s === n) return true;
  if (s.length >= n.length) return s.startsWith(n) || s.includes(n);
  return n.startsWith(s);
}

function fmtTotalDuration(secs: number | null): string {
  if (!secs) return "--";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

/** Safe for API values that may be missing or serialized oddly (avoids NaN in String.repeat → crash). */
function energyBar(val: number | null | undefined): string {
  if (val == null) return "░░░░░";
  const n = Number(val);
  if (!Number.isFinite(n)) return "░░░░░";
  const filled = Math.max(0, Math.min(5, Math.round(n * 5)));
  return "█".repeat(filled) + "░".repeat(5 - filled);
}

/** BPM display — backend may send numbers; never call .toFixed on non-numbers (would white-screen React). */
function formatBpm(bpm: number | null | undefined, decimals: number): string {
  if (bpm == null) return "—";
  const n = Number(bpm);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(decimals);
}

const KEY_COMPAT_COLOR: Record<string, string> = {
  perfect: "#22c55e",
  compatible: "#eab308",
  risky: "#ef4444",
};

/** Client-side transition for manual reorder (no backend round-trip). Mirrors SetGeneratorService + TransitionNarrative. */
function computeTransition(from: Track, to: Track, profileRaw: string): TransitionSuggestion {
  const profile = parseSetProfileNarrative(profileRaw);
  const bpmA = from.bpm ?? 128;
  const bpmB = to.bpm ?? 128;
  const bpmDelta = Math.abs(bpmA - bpmB);
  const keyA = from.camelotCode ?? "?";
  const keyB = to.camelotCode ?? "?";
  const camelotPos = (c: string) => parseInt(c.replace(/[AB]/g, ""), 10) || 1;
  const modeA = keyA.endsWith("A");
  const modeB = keyB.endsWith("A");
  const diff = Math.abs(camelotPos(keyA) - camelotPos(keyB));
  const circular = Math.min(diff, 12 - diff);
  const camelot = circular + (modeA === modeB ? 0 : 1);
  const keyCompat: "perfect" | "compatible" | "risky" =
    camelot <= 1 ? "perfect" : camelot <= 3 ? "compatible" : "risky";
  let technique: string;
  if (bpmDelta <= 3 && camelot <= 1) technique = "long beatmatch blend (32+ bars)";
  else if (bpmDelta <= 8) technique = "beatmatch fade (16 bars)";
  else if (bpmDelta <= 15) technique = "filter sweep + tempo nudge";
  else technique = "echo out / drop in";
  const energyA = from.energyLevel ?? 0.5;
  const energyB = to.energyLevel ?? 0.5;
  const energyJump = energyB - energyA;
  const slice = (t: Track) => ({
    bpm: t.bpm,
    camelotCode: t.camelotCode,
    energyLevel: t.energyLevel,
    danceability: t.danceability,
  });
  const why = buildWhyLine(slice(from), slice(to), profile, bpmDelta, camelot);
  const notes: string[] = [];
  if (bpmDelta > 10) notes.push(`${Math.round(bpmDelta)} BPM gap — use tempo nudge or double-time feel`);
  if (keyCompat === "risky") notes.push("Key clash — use a filter to mask harmonic tension");
  if (keyCompat === "perfect") notes.push("Perfect harmonic match — let the mix breathe");
  if (energyJump > 0.3) notes.push("Big energy boost — good drop moment");
  else if (energyJump < -0.3) notes.push("Energy drop — use for a breakdown or cooldown");
  const note = notes.length
    ? notes.join("; ")
    : keyCompat === "risky"
      ? "Tighten the blend — EQ or shorter overlap to ease the key change"
      : keyCompat === "perfect"
        ? "Strong harmonic lock — long blends read well here"
        : "Balanced move — keep levels even through the mix";
  return {
    fromTrackId: from.id,
    toTrackId: to.id,
    fromFilename: from.originalFilename,
    toFilename: to.originalFilename,
    technique,
    keyCompatibility: keyCompat,
    bpmDelta,
    note,
    why,
  };
}

// ── Components ────────────────────────────────────────────────────────────────

interface UploadItemProps {
  file: File;
  progress: number;
  track?: Track;
  queueKey: string;
  onRemove: (queueKey: string) => void;
}

const UploadItem: React.FC<UploadItemProps> = ({ file, progress, track, queueKey, onRemove }) => {
  const status = track?.analysisStatus;
  return (
    <div className="upload-item">
      <div className="upload-item-name">
        <span className="filename">{file.name}</span>
        <div className="upload-item-actions">
          {status && (
            <span
              className={`status-badge status-${status.toLowerCase()}`}
              title={
                status === "FAILED"
                  ? track?.analysisError || "Analysis failed — check Railway backend logs and SETPILOT_ANALYZER_BASE_URL"
                  : undefined
              }
            >
              {status === "PENDING" ? "⋯ pending" :
               status === "ANALYZING" ? "⟳ analyzing" :
               status === "COMPLETE"  ? "✓ ready" :
               status === "FAILED"    ? "✗ failed" : "• queued"}
            </span>
          )}
          <button
            type="button"
            className="upload-item-remove"
            onClick={(e) => { e.stopPropagation(); onRemove(queueKey); }}
            title="Remove track"
            aria-label="Remove track"
          >
            ✕
          </button>
        </div>
      </div>
      {progress < 100 && (
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}
      {track && status === "COMPLETE" && (
        <div className="track-meta">
          {track.provider && <span>{track.provider.replace("_", " ")}</span>}
          <span>{formatBpm(track.bpm, 1)} BPM</span>
          <span>{[track.musicalKey, track.keyMode].filter(Boolean).join(" ")}</span>
          <span className="camelot">{track.camelotCode || "—"}</span>
          <span title="Energy">{energyBar(track.energyLevel)}</span>
          <span>{fmtDuration(track.durationSeconds)}</span>
        </div>
      )}
    </div>
  );
};

interface SortableTrackRowProps {
  track: Track;
  index: number;
  transition?: TransitionSuggestion;
  onFeedback?: (trackIdA: number, trackIdB: number, thumb: "UP" | "DOWN") => void;
  ratedPairs?: Set<string>;
}

const SortableTrackRow: React.FC<SortableTrackRowProps> = ({
  track,
  index,
  transition,
  onFeedback,
  ratedPairs = new Set(),
}) => {
  const { attributes, listeners, setNodeRef, transform, transition: dndTransition, isDragging } =
    useSortable({ id: String(track.id) });

  const style = {
    transform: dndCSS.Transform.toString(transform),
    transition: dndTransition,
    opacity: isDragging ? 0.6 : 1,
  };

  const transitionKey = transition ? `${transition.fromTrackId}-${transition.toTrackId}` : "";
  const isRated = transitionKey && ratedPairs.has(transitionKey);

  return (
    <React.Fragment>
      <div ref={setNodeRef} style={style} className={`tracklist-item ${isDragging ? "tracklist-item-dragging" : ""}`}>
        <span
          className="tracklist-drag-handle"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
        >
          ⋮⋮
        </span>
        <span className="track-number">{String(index + 1).padStart(2, "0")}</span>
        <div className="tracklist-info">
          <span className="tracklist-name">{track.originalFilename.replace(/\.[^.]+$/, "")}</span>
          <div className="tracklist-meta">
            <span>{formatBpm(track.bpm, 0)} BPM</span>
            <span className="camelot-badge">{track.camelotCode || "—"}</span>
            <span>{[track.musicalKey, track.keyMode].filter(Boolean).join(" ")}</span>
            <span>{energyBar(track.energyLevel)}</span>
            <span>{fmtDuration(track.durationSeconds)}</span>
          </div>
        </div>
      </div>

      {transition && (
        <div className="transition-row">
          <div className="transition-line" />
          <div className="transition-info">
            <span
              className="compat-dot"
              style={{ color: KEY_COMPAT_COLOR[transition.keyCompatibility] }}
            >●</span>
            <span className="technique">{transition.technique}</span>
            {transition.why && (
              <span className="trans-why" title={transition.note}>{transition.why}</span>
            )}
            {!transition.why && transition.note && (
              <span className="trans-note">{transition.note}</span>
            )}
            {onFeedback && (
              <span className="transition-feedback">
                {isRated ? (
                  <span className="feedback-thanks">Thanks!</span>
                ) : (
                  <>
                    <span className="feedback-label">How was this transition?</span>
                    <button
                      type="button"
                      className="feedback-btn feedback-up"
                      onClick={() => onFeedback(transition.fromTrackId, transition.toTrackId, "UP")}
                      title="Good transition"
                      aria-label="Good transition"
                    >
                      👍
                    </button>
                    <button
                      type="button"
                      className="feedback-btn feedback-down"
                      onClick={() => onFeedback(transition.fromTrackId, transition.toTrackId, "DOWN")}
                      title="Poor transition"
                      aria-label="Poor transition"
                    >
                      👎
                    </button>
                  </>
                )}
              </span>
            )}
          </div>
          <div className="transition-line" />
        </div>
      )}
    </React.Fragment>
  );
};

const StaticTrackRow: React.FC<{ track: Track; index: number }> = ({ track, index }) => (
  <div className="tracklist-item tracklist-item-static">
    <span className="tracklist-drag-spacer" aria-hidden="true" />
    <span className="track-number">{String(index + 1).padStart(2, "0")}</span>
    <div className="tracklist-info">
      <span className="tracklist-name">{track.originalFilename.replace(/\.[^.]+$/, "")}</span>
      <div className="tracklist-meta">
        <span>{formatBpm(track.bpm, 0)} BPM</span>
        <span className="camelot-badge">{track.camelotCode || "—"}</span>
        <span>{[track.musicalKey, track.keyMode].filter(Boolean).join(" ")}</span>
        <span>{energyBar(track.energyLevel)}</span>
        <span>{fmtDuration(track.durationSeconds)}</span>
      </div>
    </div>
  </div>
);

const StaticTransitionInfo: React.FC<{ transition: TransitionSuggestion }> = ({ transition }) => (
  <div className="transition-row">
    <div className="transition-line" />
    <div className="transition-info">
      <span
        className="compat-dot"
        style={{ color: KEY_COMPAT_COLOR[transition.keyCompatibility] }}
      >
        ●
      </span>
      <span className="technique">{transition.technique}</span>
      {transition.why && (
        <span className="trans-why" title={transition.note}>
          {transition.why}
        </span>
      )}
      {!transition.why && transition.note && <span className="trans-note">{transition.note}</span>}
    </div>
    <div className="transition-line" />
  </div>
);

interface SetResultProps {
  djSet: DjSet;
  sessionId: string;
  profile: string;
  /** When true (shared link view): no drag, feedback, or email. */
  readOnly?: boolean;
}

const SetResultReadOnly: React.FC<{ djSet: DjSet; profile: string }> = ({ djSet, profile }) => {
  const orderedTracks = djSet.orderedTracks;
  const transitions = djSet.transitions;
  const getTr = (fromId: number, toId: number) =>
    transitions.find(t => t.fromTrackId === fromId && t.toTrackId === toId);

  const handlePdf = () => {
    downloadSetPdf({
      orderedTracks,
      transitions,
      totalDurationSeconds: djSet.totalDurationSeconds,
      profile: (djSet.profile ?? profile) as string,
    });
  };

  return (
    <div className="set-result">
      <div className="set-header">
        <h2>Shared set</h2>
        <div className="set-stats">
          <span>{orderedTracks.length} tracks</span>
          <span>·</span>
          <span>{fmtTotalDuration(djSet.totalDurationSeconds)}</span>
        </div>
        <div className="set-share-row">
          <button type="button" className="secondary-btn" onClick={handlePdf}>
            Download PDF
          </button>
          <Link className="secondary-btn set-share-home" to="/">
            Create your own →
          </Link>
        </div>
      </div>

      <div className="tracklist">
        {orderedTracks.map((track, idx) => {
          const nextTrack = orderedTracks[idx + 1];
          const trans = nextTrack ? getTr(track.id, nextTrack.id) : undefined;
          return (
            <React.Fragment key={track.id}>
              <StaticTrackRow track={track} index={idx} />
              {trans && <StaticTransitionInfo transition={trans} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const SetResultOwner: React.FC<Omit<SetResultProps, "readOnly">> = ({ djSet, sessionId, profile }) => {
  const [orderedTracks, setOrderedTracks] = useState<Track[]>(() => djSet.orderedTracks);
  const [transitions, setTransitions] = useState<TransitionSuggestion[]>(() => djSet.transitions);
  const [ratedPairs, setRatedPairs] = useState<Set<string>>(() => new Set());
  const initialOrderIds = djSet.orderedTracks.map(t => t.id).join(",");

  const [emailInput, setEmailInput] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "sending" | "done" | "err">("idle");
  const [emailMsg, setEmailMsg] = useState<string | null>(null);
  const [emailThanks, setEmailThanks] = useState(false);
  /* Show email block on every set result; only "Maybe later" hides it for this tab session. */
  const [emailSoftHidden, setEmailSoftHidden] = useState(() => {
    try {
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(EMAIL_SOFT_DISMISS_KEY) === "1") {
        return true;
      }
    } catch {
      /* ignore */
    }
    return false;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getTransition = (fromId: number, toId: number): TransitionSuggestion | undefined =>
    transitions.find(t => t.fromTrackId === fromId && t.toTrackId === toId);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedTracks.findIndex(t => String(t.id) === active.id);
    const newIndex = orderedTracks.findIndex(t => String(t.id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const fromOrder = orderedTracks.map(t => t.id);
    const reordered = arrayMove(orderedTracks, oldIndex, newIndex);
    const toOrder = reordered.map(t => t.id);
    logReorder(sessionId, djSet.id, fromOrder, toOrder).catch(() => {});
    const newTransitions: TransitionSuggestion[] = [];
    for (let i = 0; i < reordered.length - 1; i++) {
      newTransitions.push(computeTransition(reordered[i], reordered[i + 1], profile));
    }
    setOrderedTracks(reordered);
    setTransitions(newTransitions);
  }, [orderedTracks, sessionId, djSet.id, profile]);

  const hasUserReordered = orderedTracks.map(t => t.id).join(",") !== initialOrderIds;

  const onFeedback = useCallback((trackIdA: number, trackIdB: number, thumb: "UP" | "DOWN") => {
    const key = `${trackIdA}-${trackIdB}`;
    if (ratedPairs.has(key)) return;
    submitFeedback({
      sessionId,
      trackIdA,
      trackIdB,
      thumbRating: thumb,
      reordered: hasUserReordered,
      setId: djSet.id,
      profile,
    }).then(() => setRatedPairs(prev => new Set(prev).add(key))).catch(() => {});
  }, [sessionId, djSet.id, profile, hasUserReordered, ratedPairs]);

  const [shareUrl, setShareUrl] = useState<string | null>(() =>
    djSet.shareToken ? `${getPublicSiteOrigin()}/set/${djSet.shareToken}` : null
  );
  const [shareBusy, setShareBusy] = useState(false);

  React.useEffect(() => {
    if (djSet.shareToken) {
      setShareUrl(`${getPublicSiteOrigin()}/set/${djSet.shareToken}`);
    }
  }, [djSet.shareToken]);

  const handlePdf = useCallback(() => {
    downloadSetPdf({
      orderedTracks,
      transitions,
      totalDurationSeconds: djSet.totalDurationSeconds,
      profile: (djSet.profile ?? profile) as string,
    });
  }, [orderedTracks, transitions, djSet.totalDurationSeconds, djSet.profile, profile]);

  const handleSaveShare = useCallback(async () => {
    setShareBusy(true);
    try {
      const r = await saveSet(djSet.id, sessionId);
      const url = `${getPublicSiteOrigin()}${r.path}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
    } catch {
      /* ignore — user can copy manually */
    } finally {
      setShareBusy(false);
    }
  }, [djSet.id, sessionId]);

  return (
    <div className="set-result">
      <div className="set-header">
        <h2>Your suggested order</h2>
        <div className="set-stats">
          <span>{orderedTracks.length} tracks</span>
          <span>·</span>
          <span>{fmtTotalDuration(djSet.totalDurationSeconds)}</span>
          <span className="drag-hint">— drag to reorder</span>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={orderedTracks.map(t => String(t.id))}
          strategy={verticalListSortingStrategy}
        >
          <div className="tracklist">
            {orderedTracks.map((track, idx) => {
              const nextTrack = orderedTracks[idx + 1];
              const trans = nextTrack ? getTransition(track.id, nextTrack.id) : undefined;
              return (
                <SortableTrackRow
                  key={track.id}
                  track={track}
                  index={idx}
                  transition={trans}
                  onFeedback={onFeedback}
                  ratedPairs={ratedPairs}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {!emailSoftHidden && (
        <div className="email-soft-ask" aria-label="Optional email signup">
          {emailThanks ? (
            <p className="email-soft-ask-thanks">You’re on the list. Thanks!</p>
          ) : (
            <>
              <p className="email-soft-ask-text">
                Add your email to unlock <strong>PDF export</strong> and a <strong>shareable link</strong> — and get
                notified about new features.
              </p>
              <form
                className="email-soft-ask-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const trimmed = emailInput.trim();
                  if (!trimmed) {
                    setEmailMsg("Enter an email or tap Maybe later.");
                    return;
                  }
                  setEmailStatus("sending");
                  setEmailMsg(null);
                  submitEmailCapture({ email: trimmed, sessionId, setId: djSet.id })
                    .then(() => {
                      setEmailThanks(true);
                      setEmailStatus("idle");
                    })
                    .catch(() => {
                      setEmailStatus("err");
                      setEmailMsg("Couldn’t save — try again or skip.");
                    });
                }}
              >
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="email-soft-ask-input"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  disabled={emailStatus === "sending"}
                />
                <button type="submit" className="email-soft-ask-submit" disabled={emailStatus === "sending"}>
                  {emailStatus === "sending" ? "…" : "Notify me"}
                </button>
              </form>
              {emailMsg && <p className="email-soft-ask-hint">{emailMsg}</p>}
              <button
                type="button"
                className="email-soft-ask-dismiss"
                onClick={() => {
                  try {
                    sessionStorage.setItem(EMAIL_SOFT_DISMISS_KEY, "1");
                  } catch {
                    /* ignore */
                  }
                  setEmailSoftHidden(true);
                }}
              >
                Maybe later
              </button>
            </>
          )}
        </div>
      )}

      {emailThanks && (
        <div className="set-bottom-actions" aria-label="Export and share">
          <p className="set-bottom-actions-lede">Your email is saved — download or share your set below.</p>
          <div className="set-share-row set-share-row-bottom">
            <button type="button" className="secondary-btn" onClick={handlePdf}>
              Download PDF
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => void handleSaveShare()}
              disabled={shareBusy}
            >
              {shareBusy ? "…" : shareUrl ? "Refresh share link" : "Save & get share link"}
            </button>
          </div>
          {shareUrl && (
            <div className="share-url-box share-url-box-bottom">
              <input
                readOnly
                className="share-url-input"
                value={shareUrl}
                onFocus={e => e.target.select()}
                aria-label="Shareable link"
              />
              <button
                type="button"
                className="secondary-btn share-url-copy"
                onClick={() => void navigator.clipboard.writeText(shareUrl)}
              >
                Copy link
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SetResult: React.FC<SetResultProps> = props => {
  if (props.readOnly) {
    return <SetResultReadOnly djSet={props.djSet} profile={props.profile} />;
  }
  return <SetResultOwner djSet={props.djSet} sessionId={props.sessionId} profile={props.profile} />;
};

// ── Main App ──────────────────────────────────────────────────────────────────

type AppStep = "upload" | "analyzing" | "ready" | "generating" | "result";
type Profile = "SMOOTH" | "ENERGETIC" | "WILD";

const TALLY_EMBED_SCRIPT = "https://tally.so/widgets/embed.js";
/** Standard embed + dynamicHeight — Tally’s script resizes the iframe to content (no gap under Submit). */
const TALLY_FEEDBACK_EMBED =
  "https://tally.so/embed/RGLN7v?alignLeft=1&hideTitle=1&dynamicHeight=1";

const SetPilotApp: React.FC = () => {
  /** New id every full page load / reload — not persisted, so refresh starts an empty track session. */
  const [sessionId] = useState(() => uuidv4());
  const [step, setStep] = useState<AppStep>("upload");
  const [uploadQueue, setUploadQueue] = useState<Map<string, { file: File; progress: number; track?: Track }>>(new Map());
  const [tracks, setTracks] = useState<Track[]>([]);
  const [djSet, setDjSet] = useState<DjSet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile>(() => {
    const stored = localStorage.getItem("setpilot_profile");
    if (stored === "SMOOTH" || stored === "ENERGETIC" || stored === "WILD") return stored;
    return "SMOOTH";
  });
  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    return localStorage.getItem(ONBOARDING_KEY) !== "1";
  });
  const [onboardingDontShow, setOnboardingDontShow] = useState<boolean>(true);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const stopUploadRequestedRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);
  const [playlistUrlInput, setPlaylistUrlInput] = useState("");
  const [isImportingPlaylist, setIsImportingPlaylist] = useState(false);
  const stepRef = useRef<AppStep>(step);
  const djSetRef = useRef<DjSet | null>(djSet);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    djSetRef.current = djSet;
  }, [djSet]);

  const queueHasIncompleteAnalysis = React.useMemo(() => {
    for (const [, e] of uploadQueue) {
      const st = e.track?.analysisStatus;
      if (!e.track) return true;
      if (st === "PENDING" || st === "ANALYZING") return true;
    }
    return false;
  }, [uploadQueue]);

  /**
   * Move to "ready" when every queue row has finished analysis.
   * Do not rely only on the poll effect: when the last track becomes COMPLETE/FAILED (e.g. cached
   * analysis on upload), queueHasIncompleteAnalysis becomes false, the poller tears down, and the
   * allQueueDone branch inside setUploadQueue may never run — leaving step stuck on "analyzing".
   */
  useEffect(() => {
    if (step !== "analyzing") return;
    if (uploadQueue.size === 0) return;
    const allFinished = Array.from(uploadQueue.values()).every(
      (e) =>
        e.track &&
        (e.track.analysisStatus === "COMPLETE" || e.track.analysisStatus === "FAILED")
    );
    if (allFinished) {
      setStep("ready");
    }
  }, [step, uploadQueue]);

  // Tally iframe: load embed.js once so dynamicHeight can shrink the iframe to the form (removes extra white space).
  useEffect(() => {
    const w = window as Window & { Tally?: { loadEmbeds: () => void } };
    const load = () => {
      try {
        w.Tally?.loadEmbeds();
      } catch {
        /* ignore */
      }
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TALLY_EMBED_SCRIPT}"]`);
    if (existing) {
      if (w.Tally) load();
      else existing.addEventListener("load", load);
      return;
    }
    const script = document.createElement("script");
    script.src = TALLY_EMBED_SCRIPT;
    script.async = true;
    script.onload = load;
    document.body.appendChild(script);
  }, []);

  // Poll until every queue row is COMPLETE or FAILED. Must keep running while step is "ready"
  // if some rows are still pending — otherwise we stop refreshing and the UI sticks on "pending".
  useEffect(() => {
    const shouldPoll =
      (step === "analyzing" || step === "ready") &&
      queueHasIncompleteAnalysis;
    if (!shouldPoll) return;

    const poll = async () => {
      try {
        const updated = await getTracks(sessionId);
        setTracks(updated);

        setUploadQueue(prev => {
          const next = new Map(prev);
          next.forEach((val, key) => {
            const existingIdRaw = (val.track as any)?.id;
            const existingIdNum =
              typeof existingIdRaw === "number" ? existingIdRaw : Number(existingIdRaw);
            const tById =
              Number.isFinite(existingIdNum) ? updated.find(tr => tr.id === existingIdNum) : undefined;
            const tByFilename = updated.find(tr => trackMatchesFile(tr, val.file));
            const t = tById ?? tByFilename;
            if (t) next.set(key, { ...val, track: t });
          });
          const allQueueDone = next.size > 0 && Array.from(next.values()).every(
            e => e.track && (e.track.analysisStatus === "COMPLETE" || e.track.analysisStatus === "FAILED")
          );
          if (allQueueDone) {
            setStep(s => (s === "analyzing" ? "ready" : s));
          }
          return next;
        });
      } catch (e: any) {
        console.error("Failed to poll track statuses", e);
        setError("Couldn't refresh track status. Please try again in a moment.");
      }
    };
    poll();
    pollRef.current = setInterval(poll, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [step, sessionId, queueHasIncompleteAnalysis]);

  // Browser navigation support for result view:
  // Back from result returns to ready/upload within app, Forward returns to result.
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const stateTag = event.state?.setpilotView as string | undefined;
      if (stateTag === HISTORY_RESULT_STATE && djSetRef.current) {
        setStep("result");
        return;
      }
      if (stepRef.current === "result") {
        const completeCount = tracks.filter((t) => t.analysisStatus === "COMPLETE").length;
        setStep(completeCount >= 2 ? "ready" : "upload");
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [tracks]);

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const tooLarge = rejections.some((r) => r.errors.some((e) => e.code === "file-too-large"));
    if (tooLarge) {
      setError(`Each file must be ${MAX_UPLOAD_FILE_MB} MB or smaller.`);
    }
  }, []);

  const addImportedTracksToQueue = useCallback((importedTracks: Track[]) => {
    setUploadQueue((prev) => {
      const next = new Map(prev);
      for (const tr of importedTracks) {
        const key = `import-${tr.id}`;
        if (next.has(key)) continue;
        const syntheticFile = new File([], tr.originalFilename || `track-${tr.id}.mp3`);
        next.set(key, { file: syntheticFile, progress: 100, track: tr });
      }
      return next;
    });
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    setError(null);
    stopUploadRequestedRef.current = false;
    setIsUploading(true);
    setStep("analyzing");

    try {
      const existing = await getTracks(sessionId);
      const uploadedCount = existing.filter((t) => t.sourceType !== "PLAYLIST_IMPORT").length;
      const remaining = MAX_TRACKS_PER_SESSION - uploadedCount;
      if (remaining <= 0) {
        setError(`You can upload at most ${MAX_TRACKS_PER_SESSION} local tracks per session.`);
        setIsUploading(false);
        setStep("upload");
        return;
      }
      const filesToProcess = acceptedFiles.slice(0, remaining);
      if (acceptedFiles.length > filesToProcess.length) {
        setError(
          `Only ${filesToProcess.length} of ${acceptedFiles.length} file(s) added (${MAX_TRACKS_PER_SESSION} local uploads per session maximum).`
        );
      }

      for (const file of filesToProcess) {
        if (stopUploadRequestedRef.current) break;
        const key = `${file.name}-${file.size}`;
        setUploadQueue(prev => new Map(prev).set(key, { file, progress: 0 }));

        try {
          const controller = new AbortController();
          uploadAbortRef.current = controller;
          const track = await uploadTrack(file, sessionId, (pct) => {
            setUploadQueue(prev => {
              const next = new Map(prev);
              const entry = next.get(key);
              if (entry) next.set(key, { ...entry, progress: pct });
              return next;
            });
          }, controller.signal);

          setUploadQueue(prev => {
            const next = new Map(prev);
            const entry = next.get(key);
            if (entry) next.set(key, { ...entry, progress: 100, track });
            return next;
          });
          const updated = await getTracks(sessionId);
          setTracks(updated);
        } catch (e: any) {
          if (stopUploadRequestedRef.current) {
            break;
          }
          const msg =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            String(e);
          setError(`Failed to upload ${file.name}: ${msg}`);
        } finally {
          uploadAbortRef.current = null;
        }
      }
    } catch (e: any) {
      console.error("onDrop failed", e);
      setError(e?.message || "Something went wrong while adding tracks.");
    } finally {
      setIsUploading(false);
    }
  }, [sessionId]);

  const handleImportPlaylist = useCallback(async () => {
    const url = playlistUrlInput.trim();
    if (!url) {
      setError("Paste a YouTube playlist URL.");
      return;
    }
    setError(null);
    setIsImportingPlaylist(true);
    setStep("analyzing");
    try {
      // Import behavior: replace current session tracks with the new playlist.
      await deleteAllTracks(sessionId);
      setUploadQueue(new Map());
      setTracks([]);

      const response = await importPlaylist(sessionId, url);
      setPlaylistUrlInput("");
      setTracks(response.tracks);
      addImportedTracksToQueue(
        response.tracks.filter((t) => t.sourceType === "PLAYLIST_IMPORT")
      );
      const completeCount = response.tracks.filter((t) => t.analysisStatus === "COMPLETE").length;
      setStep(completeCount >= 2 ? "ready" : "analyzing");
      if (response.importedCount === 0) {
        setError(response.message || "No tracks were imported from this playlist.");
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Playlist import failed.";
      setError(msg);
      setStep("upload");
    } finally {
      setIsImportingPlaylist(false);
    }
  }, [playlistUrlInput, sessionId, addImportedTracksToQueue]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: MAX_UPLOAD_FILE_BYTES,
    accept: { "audio/*": [".mp3", ".wav", ".flac", ".aiff", ".m4a", ".ogg"] },
    multiple: true,
  });

  const handleGenerate = async () => {
    setError(null);
    setStep("generating");
    const trackIdsToUse = queueArr
      .filter(([, e]) => e.track?.analysisStatus === "COMPLETE")
      .map(([, e]) => e.track!.id);
    try {
      const set = await generateSet(sessionId, trackIdsToUse, profile);
      setDjSet(set);
      window.history.pushState({ setpilotView: HISTORY_RESULT_STATE }, "");
      setStep("result");
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
      setStep("ready");
    }
  };

  const handleReset = async () => {
    try {
      await deleteAllTracks(sessionId);
    } catch {
      /* still start a fresh client session */
    }
    window.location.reload();
  };

  const handleRemoveTrack = useCallback(async (queueKey: string) => {
    const entry = uploadQueue.get(queueKey);
    if (entry?.track) {
      try {
        await deleteTrack(entry.track.id);
      } catch (e: any) {
        setError(`Failed to remove: ${e.message}`);
        return;
      }
    }
    setUploadQueue(prev => {
      const next = new Map(prev);
      next.delete(queueKey);
      return next;
    });
    if (entry?.track) {
      const updated = await getTracks(sessionId);
      setTracks(updated);
    }
  }, [sessionId, uploadQueue]);

  const handleAddMoreTracks = useCallback(async () => {
    try {
      await deleteAllTracks(sessionId);
      setUploadQueue(new Map());
      setTracks([]);
      setDjSet(null);
      setStep("upload");
      setError(null);
    } catch (e: any) {
      setError(`Failed to clear: ${e.message}`);
    }
  }, [sessionId]);

  const handleRemoveAll = useCallback(async () => {
    try {
      await deleteAllTracks(sessionId);
      setUploadQueue(new Map());
      setTracks([]);
      setStep("upload");
      setError(null);
    } catch (e: any) {
      setError(`Failed to remove all: ${e.message}`);
    }
  }, [sessionId]);

  const handleStopUploading = useCallback(() => {
    stopUploadRequestedRef.current = true;
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
    // Remove any queue entries that never got an attached track.
    setUploadQueue(prev => {
      const next = new Map(prev);
      next.forEach((val, key) => {
        if (!val.track) {
          next.delete(key);
        }
      });
      return next;
    });
    setIsUploading(false);
    // Keep analysis polling running; when all current tracks finish analyzing
    // the existing effect will move step → "ready".
  }, []);

  const queueArr = Array.from(uploadQueue.entries());
  const readyCount = queueArr.filter(([, e]) => e.track?.analysisStatus === "COMPLETE").length;
  const totalCount = queueArr.length;
  const normalizedPath = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
  const isHowItWorksPage = normalizedPath === "/how-it-works";
  const isBlogPage = normalizedPath === "/blog";

  if (isHowItWorksPage) {
    return <HowItWorksPage />;
  }
  if (isBlogPage) {
    return <BlogPage />;
  }

  const siteOrigin = getPublicSiteOrigin() || "https://setpilot.org";

  return (
    <div className="app">
      <Helmet>
        <title>SetPilot — DJ Set Generator | Harmonic Mixing &amp; Playlist Optimizer</title>
        <meta
          name="description"
          content="DJ set generator and playlist optimizer: harmonic mixing, BPM matching, Camelot key, and energy-aware order with explainable transitions. Free, session-based, privacy-friendly."
        />
        <link rel="canonical" href={`${siteOrigin}/`} />
        <meta property="og:title" content="SetPilot — DJ Set Generator" />
        <meta
          property="og:description"
          content="Generate smarter DJ sets: harmonic mixing, BPM alignment, and clear transition ideas from your own tracks — no login required."
        />
        <meta property="og:url" content={`${siteOrigin}/`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <style>{CSS}</style>
      <DotShaderBackground />

      {/* ── Header ── */}
      <header className="header">
        <div className="logo">
          <Link to="/" className="logo-link" aria-label="SetPilot home">
            <span className="logo-mark" aria-hidden="true">
              <img src={LOGO_IMG_SRC} alt="" width={32} height={32} decoding="async" />
            </span>
            <span className="logo-text">SetPilot</span>
          </Link>
        </div>
        <div className="nav-right">
          <nav className="header-nav-links" aria-label="Site">
            <Link to="/about">About</Link>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/contact">Contact</Link>
          </nav>
          <span className="tagline">Harmonic mixing assistant for DJs</span>
          <span className="beta-pill">Beta</span>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="main">
      <Analytics />
          <>

        {/* Onboarding – shown once per device until dismissed */}
        {showOnboarding && (
          <section className="onboarding">
            <div className="onboarding-card card-elevated">
              <div className="onboarding-grid">
                <div className="onboarding-main">
                  <p className="eyebrow">Welcome to SetPilot</p>
                  <h1 className="onboarding-title">Your assistant for planning a set that actually flows.</h1>
                  <p className="onboarding-sub">
                    Instead of scrolling playlists and guessing what comes next, SetPilot helps you sketch the
                    energy arc of a night — from the first warm-up record to the last hands-in-the-air moment.
                    It suggests; you decide.
                  </p>
                  <ul className="onboarding-list">
                    <li><span className="onboarding-dot">●</span><span>Drop your own MP3 / WAV files — each browser session is a private crate on your machine.</span></li>
                    <li><span className="onboarding-dot">●</span><span>The assistant analyzes BPM, key (Camelot), and energy, then suggests a starting order you can drag and reorder.</span></li>
                    <li><span className="onboarding-dot">●</span><span>You stay in control: pick profiles like Smooth / Energetic / Wild and drag anything around.</span></li>
                  </ul>
                  <p className="onboarding-footnote">
                    Heads up: analysis runs on free cloud resources. Longer tracks and big batches can take a minute or two.
                  </p>
                  <div className="onboarding-actions">
                    <label className="onboarding-toggle">
                      <input
                        type="checkbox"
                        checked={onboardingDontShow}
                        onChange={(e) => setOnboardingDontShow(e.target.checked)}
                      />
                      <span>Don&apos;t show this again on this device</span>
                    </label>
                    <button
                      type="button"
                      className="onboarding-cta"
                      onClick={() => {
                        if (onboardingDontShow) {
                          localStorage.setItem(ONBOARDING_KEY, "1");
                        }
                        setShowOnboarding(false);
                      }}
                    >
                      I&apos;m ready — let&apos;s go
                    </button>
                  </div>
                </div>
                <div className="onboarding-side">
                  <div className="onboarding-mini-card">
                    <h2>What the assistant shows</h2>
                    <ul>
                      <li><strong>BPM &amp; key</strong> for every track.</li>
                      <li><strong>Camelot codes</strong> so harmonic mixing is effortless.</li>
                      <li><strong>Energy levels</strong> to shape your build, peak and cooldown.</li>
                    </ul>
                  </div>
                  <div className="onboarding-mini-card">
                    <h2>Tips</h2>
                    <ul>
                      <li>Start with <strong>8–15 tracks</strong> per idea.</li>
                      <li>Include both <strong>warm-up</strong> and <strong>peak-time</strong> tunes.</li>
                      <li>Try different profiles to feel how the arc changes.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Landing hero – compact so upload card remains visible on first screen */}
        {!showOnboarding && queueArr.length === 0 && step === "upload" && (
          <section className="hero hero-compact">
            <div className="hero-left hero-left-panel">
              <h1>Upload your tracks. Plan smarter with an assistant.</h1>
              <p className="hero-sub">
                SetPilot analyzes BPM, key, and energy to suggest an explainable order with transition ideas.
              </p>
              <ul className="hero-steps">
                <li><span className="step-badge">➤</span> Drop your audio files</li>
                <li><span className="step-badge">➤</span> Pick Smooth, Energetic, or Wild</li>
                <li><span className="step-badge">➤</span> Get a suggested order you can tweak</li>
              </ul>
            </div>
            <div className="hero-right">
              <div className="hero-card">
                <div className="hero-card-header">
                  <span className="hero-chip">Free beta</span>
                  <span className="hero-chip profile-chip">{profile}</span>
                </div>
                <p className="hero-card-title">
                  <span className="hero-card-icon">🎧</span>
                  Perfect next track, every time.
                </p>
                <p className="hero-card-text">
                  “8A→9A (perfect Camelot transition on the wheel), BPM +2 for a smooth build, slight energy lift” — every transition comes with
                  a clear line of reasoning so you always know why it works.
                </p>
                <div className="hero-card-footer">
                  <span className="hero-card-dot" /> Drop tracks below to start.
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Upload zone — always visible until result */}
        {!showOnboarding && step !== "result" && (
          <section className="upload-section">
            <div className="playlist-import card-elevated">
              <p className="playlist-import-title">Import from playlist link</p>
              <p className="playlist-import-subtitle">
                Paste a YouTube playlist URL. Import replaces current session tracks.
              </p>
              <div className="playlist-import-controls">
                <input
                  type="url"
                  className="playlist-import-input"
                  placeholder="https://www.youtube.com/playlist?list=..."
                  value={playlistUrlInput}
                  onChange={(e) => setPlaylistUrlInput(e.target.value)}
                  disabled={isImportingPlaylist}
                />
                <button
                  type="button"
                  className="playlist-import-btn"
                  onClick={handleImportPlaylist}
                  disabled={isImportingPlaylist}
                >
                  {isImportingPlaylist ? "Importing..." : "Import playlist"}
                </button>
              </div>
            </div>

            <div {...getRootProps()} className={`dropzone ${isDragActive ? "dropzone-active" : ""}`}>
              <input {...getInputProps()} />
              <div className="dropzone-content">
                <span className="dropzone-icon">🎧</span>
                <p className="dropzone-primary">
                  {isDragActive ? "Drop to add tracks" : "Drop tracks here"}
                </p>
                <p className="dropzone-secondary">
                  MP3 · WAV · FLAC · AIFF · M4A · OGG · max {MAX_UPLOAD_FILE_MB} MB each · up to {MAX_TRACKS_PER_SESSION}{" "}
                  local uploads per session
                </p>
              </div>
            </div>

            {queueArr.length > 0 && (
              <div className="upload-list card-elevated">
                <div className="upload-list-header">
                  <span className="upload-list-count">{queueArr.length} track{queueArr.length !== 1 ? "s" : ""}</span>
                  <div className="upload-list-actions">
                    <button
                      type="button"
                      className="upload-remove-all-btn"
                      onClick={handleRemoveAll}
                      title="Remove all tracks"
                    >
                      Remove all
                    </button>
                    {isUploading && (
                      <button
                        type="button"
                        className="upload-stop-btn"
                        onClick={handleStopUploading}
                      >
                        Stop uploading
                      </button>
                    )}
                  </div>
                </div>
                {queueArr.map(([key, { file, progress, track }]) => (
                  <UploadItem
                    key={key}
                    queueKey={key}
                    file={file}
                    progress={progress}
                    track={track}
                    onRemove={handleRemoveTrack}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Status / action bar */}
        {!showOnboarding && step === "analyzing" && (
          <div className="status-bar">
            <span className="pulse">⟳</span>
            <span>Analyzing {totalCount} track{totalCount !== 1 ? "s" : ""} — {readyCount} ready</span>
          </div>
        )}

        {!showOnboarding && step === "ready" && readyCount >= 2 && (
          <div className="action-bar">
            <div className="ready-info">
              <span className="ready-check">✓</span>
              <span>{readyCount} tracks analyzed and ready</span>
            </div>
            <div className="profile-controls">
              <div className="profile-pills" aria-label="Set profile">
                <button
                  type="button"
                  className={`profile-pill ${profile === "SMOOTH" ? "profile-pill-active" : ""}`}
                  onClick={() => { setProfile("SMOOTH"); localStorage.setItem("setpilot_profile", "SMOOTH"); }}
                >
                  Smooth
                </button>
                <button
                  type="button"
                  className={`profile-pill ${profile === "ENERGETIC" ? "profile-pill-active" : ""}`}
                  onClick={() => { setProfile("ENERGETIC"); localStorage.setItem("setpilot_profile", "ENERGETIC"); }}
                >
                  Energetic
                </button>
                <button
                  type="button"
                  className={`profile-pill ${profile === "WILD" ? "profile-pill-active" : ""}`}
                  onClick={() => { setProfile("WILD"); localStorage.setItem("setpilot_profile", "WILD"); }}
                >
                  Wild
                </button>
              </div>
              <button className="generate-btn" onClick={handleGenerate}>
                Generate Set ▶
              </button>
            </div>
          </div>
        )}

        {!showOnboarding && step === "ready" && readyCount < 2 && (
          <div className="status-bar status-warn">
            Need at least 2 successfully analyzed tracks before the assistant can suggest an order.
          </div>
        )}

        {!showOnboarding && step === "generating" && (
          <div className="status-bar">
            <span className="pulse">◈</span>
            <span>Assistant is preparing a suggested order…</span>
          </div>
        )}

        {error && (
          <div className="error-bar">⚠ {error}</div>
        )}

        {/* Set result */}
        {step === "result" && djSet && (
          <>
            <div className="card-elevated">
              <SetResult key={djSet.id} djSet={djSet} sessionId={sessionId} profile={profile} />
            </div>
            <div className="result-actions">
              <button
                className="secondary-btn"
                onClick={() =>
                  setStep(tracks.filter(t => t.analysisStatus === "COMPLETE").length >= 2 ? "ready" : "upload")
                }
              >
                ← Back
              </button>
              <button className="secondary-btn" onClick={handleReset}>
                ↺ New session
              </button>
            </div>
          </>
        )}

        {/* Marketing sections */}
        {!showOnboarding && (
          <section className="sections-stack">
            {/* Hero strip */}
            <div className="section-hero card-elevated">
              <div className="section-hero-left">
                <p className="eyebrow">For DJs who think in stories, not playlists.</p>
                <h2 className="section-hero-title">
                  Shape the arc of your next DJ set with an assistant in minutes.
                </h2>
                <p className="section-hero-sub">
                  Drop your crate, pick a profile, and let the assistant propose an energy arc with harmonic
                  transition ideas you can actually trust on a dancefloor.
                </p>
              </div>
              <div className="section-hero-right">
                <div className="hero-visual">
                  <div className="hero-wave" />
                  <div className="hero-pill hero-pill-bpm">122 BPM · Energy 7/10</div>
                  <div className="hero-pill hero-pill-key">9A → 10A · smooth lift</div>
                </div>
              </div>
            </div>

            {/* Short how/why strip */}
            <div className="section-grid">
              <div className="section-card">
                <h3>How it works</h3>
                <ol className="how-list">
                  <li><span>1</span><p>Drop 8–15 tracks from your crate.</p></li>
                  <li><span>2</span><p>The assistant reads BPM, key and energy in the cloud.</p></li>
                  <li><span>3</span><p>Pick a profile and get a suggested order you can tweak.</p></li>
                </ol>
                <p className="how-full-guide">
                  <Link to="/how-it-works">Full guide →</Link>
                </p>
              </div>
              <div className="section-card">
                <h3>Why DJs care</h3>
                <ul className="why-list">
                  <li><strong>Never chart keys manually again.</strong>Drop tracks, get Camelot codes and BPM instantly.</li>
                  <li><strong>Prep in 5 minutes, not 2 hours.</strong>Spend your energy on the music, not the spreadsheet.</li>
                  <li><strong>Your arc, your story.</strong>The assistant proposes — you decide.</li>
                </ul>
              </div>
            </div>
          </section>
        )}
          </>

      </main>

      {/* Tally feedback — https://tally.so/r/RGLN7v */}
      <section className="feedback-section" aria-labelledby="feedback-heading">
        <h2 id="feedback-heading" className="feedback-section-title">
          Feedback
        </h2>
        <p className="feedback-section-lede">
          Help us improve SetPilot — quick form, no account needed.
        </p>
        {/* data-tally-src + embed.js: dynamicHeight fits iframe to content (no blank strip under Submit) */}
        <div className="feedback-embed-wrap">
          <iframe
            data-tally-src={TALLY_FEEDBACK_EMBED}
            title="SetPilot feedback form"
            width="100%"
            height={320}
            frameBorder={0}
            marginHeight={0}
            marginWidth={0}
            loading="lazy"
          />
        </div>
        <p className="feedback-section-fallback">
          <a href="https://tally.so/r/RGLN7v" target="_blank" rel="noopener noreferrer">
            Open form in a new tab
          </a>
        </p>
      </section>

      {/* Simple footer */}
      <footer className="footer">
        <div className="footer-inner">
          <span className="footer-brand">
            <span className="footer-mark" aria-hidden="true">
              <img src={LOGO_IMG_SRC} alt="" width={20} height={20} decoding="async" />
            </span>
            SetPilot
          </span>
          <span className="footer-text">An assistant for DJs who care about key, energy, and the story of a night.</span>
          <nav className="footer-legal" aria-label="Legal and site">
            <Link to="/privacy-policy">Privacy policy</Link>
            <Link to="/terms-of-service">Terms of service</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/about">About</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:        #0a0a0c;
    --bg2:       #121218;
    --bg3:       #1a1a22;
    --bg4:       #22222c;
    --border:    #2a2a36;
    --border-hover: #3a3a48;
    --accent:    #f97316;
    --accent-dim: rgba(249, 115, 22, 0.15);
    --accent2:   #fb923c;
    --text:      #f9fafb;
    --text-dim:  #d4d4d8;
    --muted:     #9ca3af;
    --success:   #22c55e;
    --warning:   #eab308;
    --danger:    #ef4444;
    --font:      'Syne', system-ui, sans-serif;
    --mono:      'JetBrains Mono', monospace;
  }

  body {
    background: var(--bg);
    background-image: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(249, 115, 22, 0.08), transparent);
    color: var(--text);
    font-family: var(--font);
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
  }

  .app {
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 32px 96px;
  }

  /* Generic elevated card */
  .card-elevated {
    background:
      radial-gradient(circle at top left, rgba(249,115,22,0.16), transparent 60%),
      rgba(15,23,42,0.96);
    border-radius: 20px;
    border: 1px solid rgba(148,163,184,0.35);
    box-shadow:
      0 0 0 1px rgba(15,23,42,0.9),
      0 0 24px rgba(249,115,22,0.25),
      0 22px 60px rgba(0,0,0,0.75);
  }

  /* Header */
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 28px 0 32px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 24px;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .logo-link {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: inherit;
  }

  .header-nav-links {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .header-nav-links a {
    color: var(--muted);
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
  }

  .header-nav-links a:hover {
    color: var(--accent2);
  }

  .how-full-guide {
    margin-top: 14px;
    font-size: 13px;
  }

  .how-full-guide a {
    color: var(--accent2);
    text-decoration: none;
    font-weight: 600;
  }

  .how-full-guide a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* Circular clip: hides square white padding around the vinyl artwork */
  .logo-mark {
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: var(--bg);
    box-shadow:
      0 0 0 2px var(--accent),
      0 0 14px var(--accent-dim),
      0 4px 12px rgba(0, 0, 0, 0.4);
  }

  .logo-mark img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    /* Extra zoom so the vinyl fills the circle (PNG may still have a thin light ring) */
    transform: scale(1.14);
    transform-origin: center center;
  }

  .logo-text {
    font-family: var(--font);
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: var(--text);
  }

  .tagline {
    font-size: 14px;
    color: var(--muted);
    letter-spacing: 0.02em;
    font-weight: 400;
  }

  .nav-right {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 12px;
    color: var(--muted);
  }

  .top-nav {
    display: inline-flex;
    align-items: center;
    gap: 12px;
  }

  .top-nav-link {
    color: var(--muted);
    text-decoration: none;
    font-size: 12px;
    font-family: var(--mono);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    transition: color 0.2s;
  }

  .top-nav-link:hover {
    color: var(--text);
  }

  .top-nav-link-active {
    color: var(--accent2);
  }

  .seo-page {
    padding: 26px;
    margin-bottom: 28px;
  }

  .seo-title {
    font-size: 30px;
    line-height: 1.12;
    letter-spacing: -0.03em;
    margin: 8px 0 12px;
  }

  .seo-lead {
    font-size: 14px;
    color: var(--text-dim);
    line-height: 1.6;
    margin-bottom: 18px;
    max-width: 62ch;
  }

  .seo-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .seo-card {
    border: 1px solid var(--border);
    background: var(--bg2);
    border-radius: 12px;
    padding: 14px;
  }

  .seo-card h2 {
    font-size: 15px;
    margin-bottom: 6px;
  }

  .seo-card p {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.5;
  }

  .seo-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .seo-post {
    border: 1px solid var(--border);
    background: var(--bg2);
    border-radius: 12px;
    padding: 14px;
  }

  .seo-post h2 {
    font-size: 16px;
    margin-bottom: 6px;
  }

  .seo-post p {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.5;
  }

  .beta-pill {
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(249,115,22,0.6);
    background: rgba(249,115,22,0.12);
    color: var(--accent2);
    font-size: 11px;
    font-family: var(--mono);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  /* Dropzone */
  .dropzone {
    border: 2px dashed var(--border);
    border-radius: 16px;
    padding: 56px 32px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: var(--bg2);
    box-shadow:
      0 0 0 1px rgba(15,23,42,0.9),
      0 0 20px rgba(249,115,22,0.2),
      0 18px 40px rgba(0,0,0,0.7);
  }

  .dropzone:hover, .dropzone-active {
    border-color: var(--accent);
    background: var(--accent-dim);
    box-shadow: 0 0 0 4px var(--accent-dim);
  }

  .dropzone-icon {
    font-size: 40px;
    display: block;
    margin-bottom: 16px;
    color: var(--accent);
    opacity: 0.9;
  }

  .dropzone-primary {
    font-size: 17px;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--text);
  }

  .dropzone-secondary {
    font-size: 12px;
    color: var(--muted);
    font-family: var(--mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* Upload list */
  .upload-list {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    padding: 16px 16px 18px;
    /* Prevent header buttons from visually escaping the rounded card */
    overflow: hidden;
  }

  .upload-list-header {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    width: 100%;
  }

  .upload-list-count {
    font-size: 13px;
    color: var(--muted);
    flex: 0 0 auto;
  }

  .upload-remove-all-btn {
    background: none;
    border: 1px solid var(--border);
    color: var(--muted);
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .upload-list-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
    flex: 0 0 auto;
    min-width: 0;
  }

  .upload-remove-all-btn:hover {
    color: var(--danger);
    border-color: rgba(239,68,68,0.5);
    background: rgba(239,68,68,0.08);
  }

  .upload-stop-btn {
    padding: 6px 14px;
    font-size: 12px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: rgba(148,163,184,0.1);
    color: var(--text);
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
    white-space: nowrap;
  }

  .upload-stop-btn:hover {
    background: rgba(248,250,252,0.06);
    border-color: var(--accent);
  }

  .upload-item {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px 20px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .upload-item:hover {
    border-color: var(--border-hover);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }

  .upload-item-name {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .upload-item-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .upload-item-remove {
    background: none;
    border: none;
    padding: 4px 6px;
    cursor: pointer;
    font-size: 14px;
    color: var(--muted);
    border-radius: 4px;
    transition: color 0.2s, background 0.2s;
  }

  .upload-item-remove:hover {
    color: var(--danger);
    background: rgba(239,68,68,0.1);
  }

  .filename {
    font-size: 14px;
    font-family: var(--mono);
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;
  }

  .status-badge {
    font-size: 11px;
    font-family: var(--mono);
    padding: 4px 10px;
    border-radius: 6px;
    white-space: nowrap;
    font-weight: 500;
  }

  .status-pending   { color: var(--muted);   background: rgba(113,113,122,0.2); }
  .status-analyzing { color: var(--warning);  background: rgba(234,179,8,0.15); animation: blink 1.2s ease-in-out infinite; }
  .status-complete  { color: var(--success);  background: rgba(34,197,94,0.15); }
  .status-failed    { color: var(--danger);   background: rgba(239,68,68,0.15); }

  @keyframes blink { 50% { opacity: 0.5; } }

  .progress-bar {
    height: 3px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
    margin: 8px 0;
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--accent), var(--accent2));
    border-radius: 2px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0 8px var(--accent-dim);
  }

  .track-meta {
    display: flex;
    gap: 20px;
    font-size: 11px;
    font-family: var(--mono);
    color: var(--muted);
    margin-top: 6px;
    flex-wrap: wrap;
  }

  .camelot {
    color: var(--accent);
    font-weight: 600;
  }

  /* Status / action bars */
  .status-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 24px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-top: 24px;
    font-size: 14px;
    color: var(--text-dim);
  }

  .status-warn { color: var(--warning); border-color: rgba(234,179,8,0.4); background: rgba(234,179,8,0.06); }

  .pulse {
    animation: blink 1s ease-in-out infinite;
    color: var(--accent);
    font-size: 20px;
  }

  .action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-top: 24px;
    transition: border-color 0.2s;
  }

  .action-bar:hover {
    border-color: var(--border-hover);
  }

  .ready-info {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: var(--text-dim);
  }

  .profile-controls {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .profile-pills {
    display: inline-flex;
    padding: 3px;
    border-radius: 999px;
    background: var(--bg3);
    border: 1px solid var(--border);
    gap: 2px;
  }

  .profile-pill {
    border: none;
    background: transparent;
    color: var(--muted);
    padding: 4px 10px;
    font-size: 11px;
    font-family: var(--mono);
    cursor: pointer;
    border-radius: 999px;
    transition: background 0.2s, color 0.2s;
  }

  .profile-pill-active {
    background: var(--accent);
    color: #0a0a0c;
  }

  .ready-check { color: var(--success); font-size: 20px; }

  .generate-btn {
    background: var(--accent);
    color: #0a0a0c;
    border: none;
    border-radius: 10px;
    padding: 12px 28px;
    font-family: var(--font);
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 14px var(--accent-dim);
  }

  .generate-btn:hover {
    background: var(--accent2);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px var(--accent-dim);
  }
  .generate-btn:active { transform: translateY(0); }

  .error-bar {
    margin-top: 20px;
    padding: 14px 20px;
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.3);
    border-radius: 12px;
    color: var(--danger);
    font-size: 13px;
  }

  /* Set result — inset from card edge (card-elevated has no padding) */
  .set-result {
    margin-top: 48px;
    padding: 0 24px 0 36px;
  }

  /* Inline footer inside the set card — no nested “second card” */
  .email-soft-ask {
    margin-top: 36px;
    margin-left: auto;
    margin-right: auto;
    padding-top: 28px;
    border-top: 1px solid var(--border);
    max-width: 520px;
    text-align: center;
  }

  .email-soft-ask-text {
    margin: 0 auto 16px;
    max-width: 36rem;
    font-size: 14px;
    line-height: 1.5;
    color: var(--text);
    font-weight: 500;
  }

  .email-soft-ask-form {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    justify-content: center;
  }

  .email-soft-ask-input {
    flex: 1;
    min-width: 200px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid var(--border);
    background: var(--bg2);
    color: var(--text);
    font-size: 14px;
    font-family: var(--font);
  }

  .email-soft-ask-input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-dim);
  }

  .email-soft-ask-submit {
    padding: 10px 18px;
    border-radius: 10px;
    border: none;
    background: var(--accent);
    color: #0a0a0c;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    font-family: var(--font);
    transition: background 0.2s;
  }

  .email-soft-ask-submit:hover:not(:disabled) {
    background: var(--accent2);
  }

  .email-soft-ask-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .email-soft-ask-hint {
    margin: 10px 0 0 0;
    font-size: 12px;
    color: var(--danger);
  }

  .email-soft-ask-dismiss {
    margin-top: 14px;
    margin-left: auto;
    margin-right: auto;
    display: block;
    background: none;
    border: none;
    color: var(--muted);
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
    padding: 0;
    font-family: var(--font);
  }

  .email-soft-ask-dismiss:hover {
    color: var(--text);
  }

  .email-soft-ask-thanks {
    margin: 0 auto;
    font-size: 14px;
    color: var(--success);
    font-weight: 600;
  }

  .set-header {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 28px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--border);
  }

  .set-header h2 {
    font-family: var(--font);
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--text);
  }

  .set-stats {
    display: flex;
    gap: 10px;
    align-items: center;
    font-size: 13px;
    color: var(--muted);
  }

  .drag-hint {
    font-size: 11px;
    color: var(--text-dim);
  }

  .set-bottom-actions {
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid var(--border);
  }

  .set-bottom-actions-lede {
    font-size: 14px;
    color: var(--text-dim);
    margin-bottom: 16px;
    line-height: 1.5;
  }

  .set-share-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    width: 100%;
    margin-top: 12px;
  }

  .set-share-row-bottom {
    margin-top: 0;
  }

  .share-url-box-bottom {
    margin-top: 12px;
  }

  .share-url-box {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    flex: 1;
    min-width: 200px;
  }

  .share-url-input {
    flex: 1;
    min-width: 180px;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--bg2);
    color: var(--text);
    font-size: 12px;
    font-family: var(--mono);
  }

  .set-share-home {
    text-align: center;
  }

  .tracklist-drag-spacer {
    width: 22px;
    flex-shrink: 0;
  }

  .tracklist {
    display: flex;
    flex-direction: column;
  }

  .tracklist-item {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 18px 0;
    border-bottom: 1px solid var(--border);
    transition: background 0.2s;
  }

  .tracklist-item:hover {
    background: rgba(255,255,255,0.02);
  }

  .tracklist-item-dragging {
    border-radius: 12px;
    background: var(--bg3);
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  }

  .tracklist-drag-handle {
    cursor: grab;
    color: var(--muted);
    font-size: 14px;
    padding: 6px 4px;
    user-select: none;
    flex-shrink: 0;
    transition: color 0.2s;
  }

  .tracklist-drag-handle:hover {
    color: var(--accent);
  }

  .tracklist-drag-handle:active {
    cursor: grabbing;
  }

  .track-number {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
    min-width: 28px;
    padding-top: 2px;
  }

  .tracklist-info {
    flex: 1;
  }

  .tracklist-name {
    font-size: 15px;
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
    color: var(--text);
  }

  .tracklist-meta {
    display: flex;
    gap: 16px;
    font-size: 11px;
    font-family: var(--mono);
    color: var(--muted);
    flex-wrap: wrap;
  }

  .camelot-badge {
    background: var(--accent-dim);
    color: var(--accent);
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
  }

  /* Transitions */
  .transition-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 0;
  }

  .transition-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border), transparent);
    opacity: 0.6;
  }

  .transition-info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 11px;
    color: var(--muted);
    font-family: var(--mono);
  }

  .compat-dot { font-size: 10px; }

  .technique {
    color: var(--text-dim);
    font-weight: 600;
  }

  .trans-why {
    color: var(--muted);
    font-size: 10px;
    letter-spacing: 0.02em;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .trans-note {
    color: var(--muted);
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .transition-feedback {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
  }

  .feedback-label {
    font-size: 10px;
    color: var(--text-dim);
    white-space: nowrap;
  }

  .feedback-btn {
    padding: 4px 8px;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: var(--bg2);
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s, border-color 0.2s;
  }

  .feedback-btn:hover {
    background: var(--bg3);
    border-color: var(--accent);
  }

  .feedback-thanks {
    font-size: 11px;
    color: var(--accent);
    font-weight: 600;
  }

  /* Result actions */
  .result-actions {
    display: flex;
    gap: 14px;
    margin-top: 32px;
  }

  .secondary-btn {
    background: var(--bg2);
    color: var(--text-dim);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 22px;
    font-family: var(--font);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .secondary-btn:hover {
    color: var(--text);
    border-color: var(--accent);
    background: var(--accent-dim);
  }

  .main {
    max-width: 960px;
    margin: 0 auto;
    padding: 32px 20px 60px;
  }

  /* Section spacing */
  .onboarding {
    margin-bottom: 32px;
  }

  .hero {
    margin-bottom: 32px;
  }

  .upload-section {
    margin-bottom: 72px;
  }

  .playlist-import {
    padding: 16px;
    margin-bottom: 14px;
  }

  .playlist-import-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
  }

  .playlist-import-subtitle {
    font-size: 12px;
    color: var(--muted);
    margin-top: 4px;
    margin-bottom: 10px;
  }

  .playlist-import-controls {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .playlist-import-input {
    flex: 1;
    min-width: 260px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--bg2);
    color: var(--text);
    padding: 10px 12px;
    font-size: 13px;
    font-family: var(--mono);
  }

  .playlist-import-input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-dim);
  }

  .playlist-import-btn {
    border: 1px solid rgba(249,115,22,0.55);
    background: rgba(249,115,22,0.12);
    color: var(--accent2);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    font-family: var(--mono);
    cursor: pointer;
  }

  .playlist-import-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  /* Marketing sections */
  .sections-stack {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 48px;
    margin-bottom: 32px;
  }

  .section-hero {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2.5fr);
    gap: 28px;
    padding: 26px 26px 22px;
  }

  .section-hero-left {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .eyebrow {
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
    font-family: var(--mono);
  }

  .section-hero-title {
    font-size: 28px;
    line-height: 1.15;
    letter-spacing: -0.03em;
    color: var(--text);
  }

  .section-hero-sub {
    font-size: 14px;
    color: var(--text-dim);
    line-height: 1.6;
    max-width: 34rem;
  }

  .hero-points {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    color: var(--muted);
  }

  .section-hero-right {
    display: flex;
    justify-content: flex-end;
    align-items: stretch;
  }

  .hero-visual {
    position: relative;
    width: 100%;
    max-width: 320px;
    border-radius: 18px;
    padding: 16px 14px;
    background:
      radial-gradient(circle at top left, rgba(249,115,22,0.3), transparent 60%),
      radial-gradient(circle at bottom right, rgba(59,130,246,0.24), transparent 55%),
      #020617;
    border: 1px solid rgba(148,163,184,0.45);
    box-shadow: 0 20px 55px rgba(0,0,0,0.75);
    overflow: hidden;
  }

  .hero-wave {
    position: absolute;
    inset: 28% 8% 18%;
    background: linear-gradient(90deg, rgba(249,115,22,0.2), rgba(56,189,248,0.2));
    mask-image: radial-gradient(circle at 10% 0, transparent 0, black 30%), linear-gradient(#000, #000);
    opacity: 0.8;
  }

  .hero-pill {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 11px;
    font-family: var(--mono);
    background: rgba(15,23,42,0.92);
    border: 1px solid rgba(148,163,184,0.5);
    color: var(--text-dim);
    box-shadow: 0 8px 20px rgba(0,0,0,0.7);
  }

  .hero-pill-bpm {
    margin-bottom: 8px;
  }

  .hero-pill-key {
    margin-top: auto;
    align-self: flex-end;
  }

  .section-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  .section-card {
    background: var(--bg2);
    border-radius: 18px;
    border: 1px solid var(--border);
    padding: 18px 18px 16px;
    box-shadow:
      0 0 0 1px rgba(15,23,42,0.9),
      0 0 18px rgba(15,118,110,0.2),
      0 12px 28px rgba(0,0,0,0.45);
  }

  .section-card h2 {
    font-size: 17px;
    margin-bottom: 10px;
  }

  .how-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 13px;
    color: var(--muted);
  }

  .how-list li {
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }

  .how-list li span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: var(--accent-dim);
    color: var(--accent2);
    font-size: 11px;
    font-family: var(--mono);
    flex-shrink: 0;
  }

  .how-list li p {
    margin: 0;
  }

  .why-list {
    list-style: none;
    padding: 0;
    margin: 0 0 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    color: var(--muted);
  }

  .why-list strong {
    color: var(--text-dim);
  }

  .section-footnote {
    font-size: 11px;
    color: var(--muted);
    margin-top: 4px;
  }

  /* Onboarding */
  .onboarding-card {
    position: relative;
    overflow: hidden;
  }

  .onboarding-card::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 1px solid rgba(248,250,252,0.04);
    pointer-events: none;
  }

  .onboarding-grid {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    gap: 24px;
    padding: 26px 26px 22px;
  }

  .onboarding-main {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .onboarding-side {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .onboarding-mini-card {
    background: rgba(10,10,12,0.85);
    border-radius: 14px;
    border: 1px solid rgba(148,163,184,0.45);
    padding: 12px 14px 10px;
    box-shadow: 0 14px 30px rgba(0,0,0,0.6);
    font-size: 12px;
    color: var(--muted);
  }

  .onboarding-mini-card h2 {
    font-size: 13px;
    margin-bottom: 6px;
    color: var(--text-dim);
  }

  .onboarding-mini-card ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .onboarding-title {
    font-size: 30px;
    line-height: 1.15;
    letter-spacing: -0.03em;
    margin-bottom: 14px;
    color: var(--text);
  }

  .onboarding-sub {
    font-size: 15px;
    color: var(--text-dim);
    line-height: 1.5;
    margin-bottom: 16px;
    max-width: 38rem;
  }

  .onboarding-list {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 14px;
    color: var(--muted);
  }

  .onboarding-list li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .onboarding-dot {
    font-size: 10px;
    color: var(--accent2);
    margin-top: 4px;
  }

  .onboarding-footnote {
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 18px;
  }

  .onboarding-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    align-items: center;
  }

  .onboarding-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--muted);
  }

  .onboarding-toggle input {
    accent-color: var(--accent);
  }

  .onboarding-cta {
    background: var(--accent);
    color: #0a0a0c;
    border: none;
    border-radius: 999px;
    padding: 12px 26px;
    font-family: var(--font);
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px var(--accent-dim);
    white-space: nowrap;
  }

  .onboarding-cta:hover {
    background: var(--accent2);
    transform: translateY(-1px);
    box-shadow: 0 6px 22px var(--accent-dim);
  }

  /* Hero */
  .hero {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    gap: 20px;
    align-items: center;
    margin-bottom: 20px;
  }

  .hero-left-panel {
    background:
      radial-gradient(circle at top left, rgba(15,23,42,0.08), transparent 70%),
      rgba(15,23,42,0.12);
    border-radius: 14px;
    padding: 14px 16px 12px;
    border: 1px solid rgba(148,163,184,0.28);
    box-shadow:
      0 0 0 1px rgba(15,23,42,0.9),
      0 0 22px rgba(249,115,22,0.2),
      0 14px 30px rgba(0,0,0,0.5);
    backdrop-filter: blur(24px);
  }

  .hero-left h1 {
    font-size: 24px;
    line-height: 1.12;
    letter-spacing: -0.03em;
    margin-bottom: 8px;
    color: var(--text);
  }

  .hero-sub {
    font-size: 13px;
    color: var(--text-dim);
    max-width: 34rem;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  .hero-steps {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    color: var(--muted);
  }

  .step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: var(--accent-dim);
    color: var(--accent2);
    font-size: 9px;
    font-weight: 700;
    margin-right: 8px;
  }

  .hero-footnote {
    font-size: 11px;
    color: var(--muted);
  }

  .hero-right {
    display: flex;
    justify-content: flex-end;
  }

  .hero-card {
    background:
      radial-gradient(circle at top left, rgba(249,115,22,0.18), transparent 65%),
      rgba(15,23,42,0.14);
    border-radius: 14px;
    padding: 12px 12px 10px;
    border: 1px solid rgba(148,163,184,0.26);
    box-shadow:
      0 0 0 1px rgba(15,23,42,0.9),
      0 0 18px rgba(249,115,22,0.25),
      0 12px 28px rgba(0,0,0,0.45);
    backdrop-filter: blur(24px);
    max-width: 280px;
  }

  .hero-card-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .hero-chip {
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.06);
    background: rgba(0,0,0,0.3);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-dim);
  }

  .profile-chip {
    border-color: var(--accent-dim);
    color: var(--accent2);
  }

  .hero-card-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hero-card-icon {
    font-size: 16px;
  }

  .hero-card-text {
    font-size: 11px;
    color: var(--muted);
    line-height: 1.5;
    margin-bottom: 6px;
  }

  .hero-card-footer {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--text-dim);
  }

  .hero-compact .hero-footnote {
    display: none;
  }

  .hero-card-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: radial-gradient(circle, var(--accent2), transparent);
  }

  /* Tally feedback — light “paper” card so the embedded form keeps readable text (see Tally embed docs) */
  .feedback-section {
    max-width: 1120px;
    margin: 48px auto 0;
    padding: 0 32px 8px;
  }

  .feedback-section-title {
    font-family: var(--font);
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--text);
    margin-bottom: 8px;
  }

  .feedback-section-lede {
    font-size: 13px;
    color: var(--muted);
    margin-bottom: 20px;
    line-height: 1.5;
    max-width: 42rem;
  }

  .feedback-embed-wrap {
    max-width: 280px;
    margin: 0;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(148, 163, 184, 0.45);
    background: #f8fafc;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.6) inset,
      0 24px 48px rgba(0, 0, 0, 0.35),
      0 0 0 1px rgba(15, 23, 42, 0.2);
  }

  .feedback-embed-wrap iframe {
    display: block;
    width: 100%;
    min-height: 0;
    background: #fff;
  }

  .feedback-section-fallback {
    margin-top: 14px;
    font-size: 12px;
    color: var(--muted);
    text-align: left;
    max-width: 280px;
    margin-left: 0;
    margin-right: 0;
  }

  .feedback-section-fallback a {
    color: var(--accent2);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .feedback-section-fallback a:hover {
    color: var(--accent);
  }

  /* Footer */
  .footer {
    border-top: 1px solid var(--border);
    margin-top: 32px;
    padding: 16px 0 32px;
  }

  .footer-inner {
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 32px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: var(--muted);
  }

  .footer-legal {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 100%;
  }

  @media (min-width: 880px) {
    .footer-legal {
      flex: 0 1 auto;
    }
  }

  .footer-legal a {
    color: var(--muted);
    text-decoration: none;
    font-size: 12px;
  }

  .footer-legal a:hover {
    color: var(--accent2);
  }

  .footer-brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font);
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text-dim);
  }

  .footer-mark {
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    padding: 4px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: var(--bg);
    opacity: 0.95;
    box-shadow:
      0 0 0 1px var(--accent),
      0 0 8px var(--accent-dim),
      0 2px 8px rgba(0, 0, 0, 0.35);
  }

  .footer-mark img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
    transform-origin: center center;
  }
`;

const SharedSetPage: React.FC<{ token: string }> = ({ token }) => {
  const [djSet, setDjSet] = useState<DjSet | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    getSharedSet(token)
      .then(d => {
        if (!cancelled) setDjSet(d);
      })
      .catch(() => {
        if (!cancelled) setLoadErr("This set isn’t available. The link may be invalid or expired.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const prof: Profile =
    djSet?.profile === "ENERGETIC" || djSet?.profile === "WILD" || djSet?.profile === "SMOOTH"
      ? djSet.profile
      : "SMOOTH";

  const siteOrigin = getPublicSiteOrigin() || "https://setpilot.org";
  const pageUrl = `${siteOrigin}/set/${encodeURIComponent(token)}`;
  const ogImageUrl = getSharedSetOgImageUrl(token);
  const trackCount = djSet?.orderedTracks?.length ?? 0;
  const ogTitle =
    djSet && !loadErr
      ? `Shared DJ set (${trackCount} tracks) · SetPilot`
      : "Shared DJ set · SetPilot";
  const ogDescription =
    djSet && !loadErr
      ? `${prof} profile · harmonic mixing preview on SetPilot — BPM, key, and energy-aware order.`
      : "Open a shared DJ set on SetPilot — harmonic mixing assistant.";
  const showOgCard = Boolean(djSet && !loadErr);

  return (
    <div className="app">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        {showOgCard && (
          <>
            <meta property="og:image" content={ogImageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={ogImageUrl} />
          </>
        )}
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
      </Helmet>
      <style>{CSS}</style>
      <DotShaderBackground />
      <header className="header">
        <div className="logo">
          <Link
            to="/"
            className="logo-link"
            style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 8 }}
          >
            <span className="logo-mark" aria-hidden="true">
              <img src={LOGO_IMG_SRC} alt="" width={32} height={32} decoding="async" />
            </span>
            <span className="logo-text">SetPilot</span>
          </Link>
        </div>
        <div className="nav-right">
          <span className="tagline">Harmonic mixing assistant for DJs</span>
          <span className="beta-pill">Beta</span>
        </div>
      </header>
      <main className="main">
        <Analytics />
        {loading && (
          <div className="status-bar">
            <span className="pulse">⟳</span>
            <span>Loading shared set…</span>
          </div>
        )}
        {loadErr && <div className="error-bar">⚠ {loadErr}</div>}
        {!loading && djSet && (
          <div className="card-elevated">
            <SetResult djSet={djSet} sessionId="" profile={prof} readOnly />
          </div>
        )}
      </main>
      <footer className="footer">
        <div className="footer-inner">
          <span className="footer-brand">
            <span className="footer-mark" aria-hidden="true">
              <img src={LOGO_IMG_SRC} alt="" width={20} height={20} decoding="async" />
            </span>
            SetPilot
          </span>
          <span className="footer-text">An assistant for DJs who care about key, energy, and the story of a night.</span>
          <nav className="footer-legal" aria-label="Legal and site">
            <Link to="/privacy-policy">Privacy policy</Link>
            <Link to="/terms-of-service">Terms of service</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/about">About</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

function SharedSetRoute() {
  const { token } = useParams<{ token: string }>();
  if (!token || !/^[a-z0-9]{8,64}$/i.test(token)) {
    return <Navigate to="/" replace />;
  }
  return <SharedSetPage token={token.toLowerCase()} />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/set/:token" element={<SharedSetRoute />} />
      <Route path="/*" element={<SetPilotApp />} />
    </Routes>
  );
}
